//
//  describe-workspaces.ts
//  Supernova CLI
//
//  Created by Jiri Trecak.
//  Copyright © Supernova.io. All rights reserved.
//

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports

import { Flags } from "@oclif/core"
import { SentryTraced } from "@sentry/nestjs"
import * as sdkTypes from "@supernovaio/sdk"
import { createRequire } from "node:module"
import { z, ZodType } from "zod"

import { apiUrlForEnvironment, commonFlags, getTargetEnv, SentryCommand } from "../types/index.js"

const require = createRequire(import.meta.url)
const sdkProvider = require("@supernovaio/sdk") as typeof sdkTypes

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Definition

interface DescribeWorkspacesFlags {
  apiKey: string
  proxyUrl?: string
}

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Configuration

const DescribeWorkspaceConfigSchema = z
  .object({
    apiKey: z.string(),
    proxyUrl: z.string().url().optional(),
    workspaceId: z.string(),
  })
  .transform(data => ({
    ...data,
  }))

type DescribeWorkspaceConfig = z.infer<typeof DescribeWorkspaceConfigSchema>

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation

/** Command that describes the structure of provided design system */
export class DescribeWorkspaces extends SentryCommand<DescribeWorkspaceConfig> {
  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command configuration

  // How this command can be run
  static aliases = ["describe-workspaces"]
  // Command help description
  static description =
    "Describe structure of all workspaces and design systems available under those workspaces available for specified API key"
  // Examples how to use the command
  static examples = [`$ @supernovaio/cli describe-workspaces --apiKey="{xxx-xxx-xxx}"`]
  // Static flags to enable / disable features
  static flags = {
    ...commonFlags,
    apiKey: Flags.string({ description: "API key to use for accessing Supernova instance", required: true }),
    proxyUrl: Flags.string({
      description: "When set, CLI will use provided proxy URL for all requests",
      hidden: true,
      required: false,
    }),
  }

  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command runtime

  get commandId(): string {
    return DescribeWorkspaces.id
  }

  get configSchema(): ZodType<DescribeWorkspaceConfig> {
    return DescribeWorkspaceConfigSchema
  }

  @SentryTraced()
  async getWorkspaces(flags: DescribeWorkspacesFlags): Promise<{
    instance: sdkTypes.Supernova
    workspaces: Array<sdkTypes.Workspace>
  }> {
    if (!flags.apiKey || flags.apiKey.length === 0) {
      throw new Error(`API key must not be empty`)
    }

    // Create instance for prod / dev
    const apiUrl = apiUrlForEnvironment(getTargetEnv())
    const sdkInstance = new sdkProvider.Supernova(flags.apiKey, {
      apiUrl,
    })
    const user = await sdkInstance.me.me()
    const workspaces = await sdkInstance.workspaces.workspaces(user.id)
    return {
      instance: sdkInstance,
      workspaces,
    }
  }

  @SentryTraced()
  async run(): Promise<void> {
    const { flags } = await this.parse(DescribeWorkspaces)

    // Get workspaces
    const { instance, workspaces } = await this.getWorkspaces(flags)
    this.log(`\n`)

    for (const workspace of workspaces) {
      // Get design systems and log
      const designSystems = await instance.designSystems.designSystems(workspace.id)
      this.log(`↳ Workspace "${workspace.profile.name}", id: ${workspace.id}`)
      for (const designSystem of designSystems) {
        this.log(`  ↳ Design system "${designSystem.name}", id: ${designSystem.id}`)

        const version = await instance.versions.getActiveVersion(designSystem.id)
        if (!version) {
          this.log(`Design system  ${designSystem.id} active version not found or not available under provided API key`)
          continue
        }

        const id: sdkTypes.RemoteVersionIdentifier = { designSystemId: designSystem.id, versionId: version.id }
        const brands = await instance.brands.getBrands(id)
        const themes = await instance.tokens.getTokenThemes(id)
        for (const brand of brands) {
          this.log(`    ↳ Brand: "${brand.name}", id: ${brand.id}`)
          const brandThemes = themes.filter(t => t.brandId === brand.id)
          if (brandThemes.length > 0) {
            // eslint-disable-next-line max-depth
            for (const theme of brandThemes) {
              this.log(`      ↳ Theme: "${theme.name}", id: ${theme.id}`)
            }
          } else {
            this.log(`      ↳ No themes defined in this brand`)
          }
        }
      }
    }

    this.log("\nDone")
  }
}
